import { gunzipSync, strFromU8 } from 'fflate';

import {
  type E2eMarkerWindow,
  isDesktopE2eBuild,
  isNativeDesktopE2eLane,
} from './desktop-e2e-lane';
import type { FlagOverrideMap, FlagOverrideValue, FlagProvider } from './overrides';
import { createSnapshotOverride, isSnapshotFlagOverride } from './snapshot-override';

export { isDesktopE2eBuild };

const currentWindow = (): E2eMarkerWindow =>
  typeof window === 'undefined' ? undefined : window;

/**
 * Whether Flagsmith is frozen: a desktop e2e build on a page no Playwright
 * lane drives, which in practice is the native lane.
 *
 * Frozen means `OverrideOpenFeatureProvider` never reads a live value: a flag
 * resolves to the build's Flagsmith pin, or else to its call-site default.
 *
 * The desktop CDP lane runs the same build but is excluded. Its specs pin
 * Flagsmith per spec by intercepting Flagsmith's response, which a frozen
 * provider would ignore, and a build pin would override. It marks its pages
 * with `window.__IS_E2E__` from an init script, which runs before any app
 * script, so the marker is already set when this is read at boot. The native
 * lane cannot inject scripts, so it never sets it.
 *
 * One document escapes that: the one the app boots before the CDP lane
 * attaches. It reads as frozen. The lane's route pins never reached that
 * document either, since they are installed after attach, and every load
 * after attach carries the marker.
 */
export const isFlagsmithFrozen = isNativeDesktopE2eLane;

export const COMPRESSED_E2E_FLAGS_PREFIX = 'gzip-base64:';

const decodeFlagList = (raw: string): string => {
  if (!raw.startsWith(COMPRESSED_E2E_FLAGS_PREFIX)) return raw;
  const encoded = raw.slice(COMPRESSED_E2E_FLAGS_PREFIX.length);
  const bytes = Uint8Array.from(atob(encoded), (character) => character.charCodeAt(0));
  return strFromU8(gunzipSync(bytes));
};

/**
 * The provider-scoped flags a desktop e2e build boots with overridden.
 * Flagsmith entries apply only where Flagsmith is frozen (`isFlagsmithFrozen`).
 *
 * The web e2e lane pins these by intercepting the providers' requests. The
 * desktop native lane drives the OS accessibility tree and cannot intercept,
 * so the e2e build bakes the same provider-scoped lists into
 * `NEXT_PUBLIC_E2E_FLAG_OVERRIDES`
 * (`scripts/lib/e2e-flag-overrides.ts`) and the dev-tools override store
 * starts from it. One list, both lanes.
 *
 * Empty unless this is a non-production desktop e2e build. That is a RUNTIME
 * refusal, not dead-code elimination: the values arrive through the `env`
 * parameter (so tests can pass their own), which the bundler cannot fold the
 * way it folds a direct `process.env.NEXT_PUBLIC_ENVIRONMENT` comparison like
 * `scribeHttpErrorHandler.ts`'s. So this body ships in production bundles and
 * returns `{}` there — and the publish workflows never set either variable, so
 * a shipped build has no list to read in any case.
 */
export const readE2eFlagDefaults = (
  env: {
    NEXT_PUBLIC_ENVIRONMENT?: string;
    NEXT_PUBLIC_TAURI_E2E?: string;
    NEXT_PUBLIC_E2E_FLAG_OVERRIDES?: string;
  } = {
    NEXT_PUBLIC_ENVIRONMENT: process.env.NEXT_PUBLIC_ENVIRONMENT,
    NEXT_PUBLIC_TAURI_E2E: process.env.NEXT_PUBLIC_TAURI_E2E,
    NEXT_PUBLIC_E2E_FLAG_OVERRIDES: process.env.NEXT_PUBLIC_E2E_FLAG_OVERRIDES,
  },
  win: E2eMarkerWindow = currentWindow()
): FlagOverrideMap => {
  if (!isDesktopE2eBuild(env)) return {};
  // Flagsmith pins belong to the frozen (native) lane only; see isFlagsmithFrozen.
  const includeFlagsmith = isFlagsmithFrozen(env, win);
  const raw = env.NEXT_PUBLIC_E2E_FLAG_OVERRIDES;
  if (!raw) return {};

  let parsed: unknown;
  try {
    parsed = JSON.parse(decodeFlagList(raw));
  } catch {
    console.warn('[flags-dev] NEXT_PUBLIC_E2E_FLAG_OVERRIDES is not JSON; ignoring it');
    return {};
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    console.warn(
      '[flags-dev] NEXT_PUBLIC_E2E_FLAG_OVERRIDES is not an object; ignoring it'
    );
    return {};
  }

  const overrides: Record<string, FlagOverrideValue> = {};
  let inputCount = 0;
  const providers: FlagProvider[] = ['posthog', 'flagsmith'];
  for (const provider of providers) {
    if (provider === 'flagsmith' && !includeFlagsmith) continue;
    const providerFlags = (parsed as Record<string, unknown>)[provider];
    if (
      providerFlags === undefined ||
      !providerFlags ||
      typeof providerFlags !== 'object' ||
      Array.isArray(providerFlags)
    ) {
      if (providerFlags !== undefined) inputCount += 1;
      continue;
    }
    for (const [flagKey, value] of Object.entries(providerFlags)) {
      inputCount += 1;
      if (value === true) overrides[`${provider}:${flagKey}`] = 'on';
      else if (value === false) overrides[`${provider}:${flagKey}`] = 'off';
      else if (typeof value === 'string' && value) {
        overrides[`${provider}:${flagKey}`] = `variant:${value}`;
      } else if (provider === 'flagsmith' && isSnapshotFlagOverride(value)) {
        overrides[`${provider}:${flagKey}`] = createSnapshotOverride(value);
      }
    }
  }
  inputCount += Object.keys(parsed).filter(
    (key) => !(providers as string[]).includes(key)
  ).length;
  // Counts only, never keys or values: a partial list is as quiet a failure as
  // an empty one, and this line is what tells the two apart in the app log.
  const dropped = inputCount - Object.keys(overrides).length;
  if (dropped > 0) {
    console.warn(
      `[flags-dev] NEXT_PUBLIC_E2E_FLAG_OVERRIDES: ignored ${dropped} entr${dropped === 1 ? 'y' : 'ies'} that were not a boolean, variant, or Flagsmith snapshot`
    );
  }
  return overrides;
};

/**
 * The pinned defaults with whatever is stored laid on top, key by key.
 *
 * A stored map does not replace the defaults wholesale: a spec that pins one
 * flag through the dev-tools panel (`installDevToolsFlagOverrides`) stores only
 * that key, and every other pin must survive it. Where both name a flag, the
 * stored choice wins, so a spec can still opt a pinned flag back in.
 */
export const layerOverDefaults = (
  stored: FlagOverrideMap,
  defaults: FlagOverrideMap
): FlagOverrideMap =>
  Object.keys(defaults).length === 0 ? stored : { ...defaults, ...stored };
